/**
 * 在两个数字之间生成N个有序随机数
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @param {number} count - 生成数量
 * @param {number} defaultDecimals - 默认小数位数，默认6位
 * @returns {string[]} 有序的随机数字符串数组
 */
function generateOrderedRandomNumbers(min, max, count, defaultDecimals = 6) {
  // 参数验证
  if (!count || count <= 0) {
    return []
  }

  // 处理超长数字字符串，避免精度丢失
  let numMin, numMax
  let isLargeNumber = false

  if (typeof min === 'string' && typeof max === 'string') {
    // 检查是否为超长整数字符串
    const minIsInteger = /^-?\d+$/.test(min)
    const maxIsInteger = /^-?\d+$/.test(max)

    if (minIsInteger && maxIsInteger && (min.length > 15 || max.length > 15)) {
      // 超长整数，使用BigInt处理
      isLargeNumber = true
      numMin = BigInt(min)
      numMax = BigInt(max)

      if (numMin >= numMax) {
        throw new Error('min must be less than max')
      }
    } else {
      // 普通数字或小数，使用parseFloat
      numMin = parseFloat(min)
      numMax = parseFloat(max)

      if (isNaN(numMin) || isNaN(numMax)) {
        throw new Error('参数min和max必须是有效的数字或数字字符串')
      }
      if (numMin >= numMax) throw new Error('min must be less than max')
    }
  } else {
    // 至少有一个不是字符串，按原逻辑处理
    numMin = typeof min === 'string' ? parseFloat(min) : min
    numMax = typeof max === 'string' ? parseFloat(max) : max

    if (isNaN(numMin) || isNaN(numMax)) {
      throw new Error('参数min和max必须是有效的数字或数字字符串')
    }
    if (numMin >= numMax) throw new Error('min must be less than max')
  }

  // 处理超长数字字符串的特殊逻辑
  if (isLargeNumber) {
    const range = numMax - numMin
    const uniqueNumbers = new Set()

    // 计算可能的整数数量（包含边界）
    const possibleCount = Number(range) + 1

    // 如果请求的数量超过可能的数量，调整为可能的最大数量
    const actualCount = Math.min(count, possibleCount)

    // 如果范围很小，直接生成所有可能的数字
    if (possibleCount <= count) {
      const result = []
      for (let i = 0n; i <= range; i++) {
        result.push((numMin + i).toString())
      }
      return result
    }

    // 对于大整数，我们生成范围内的随机整数
    while (uniqueNumbers.size < actualCount) {
      // 生成0到1之间的随机数，然后映射到BigInt范围
      const randomFactor = Math.random()
      const randomBigInt =
        numMin + BigInt(Math.floor(Number(range) * randomFactor))
      uniqueNumbers.add(randomBigInt.toString())
    }

    // 转换为数组并排序
    const result = Array.from(uniqueNumbers)
    result.sort((a, b) => {
      const bigA = BigInt(a)
      const bigB = BigInt(b)
      if (bigA < bigB) return -1
      if (bigA > bigB) return 1
      return 0
    })

    return result.slice(0, actualCount)
  }

  if (!Number.isFinite(numMin) || !Number.isFinite(numMax)) {
    throw new Error('min and max must be finite numbers')
  }

  const range = numMax - numMin
  const numbers = new Set()

  // 计算需要的小数位数
  let requiredDecimals = defaultDecimals

  // 如果需要生成的数量很多，可能需要更多小数位来保证唯一性
  if (count > Math.pow(10, defaultDecimals)) {
    requiredDecimals = Math.ceil(Math.log10(count)) + 2
  }

  // 计算精度因子
  const precision = Math.pow(10, requiredDecimals)
  const scaledRange = Math.floor(range * precision)

  // 防止无限循环的保护机制
  let attempts = 0
  const maxAttempts = count * 1000

  // 生成唯一的随机数
  while (numbers.size < count && attempts < maxAttempts) {
    // 生成0到scaledRange之间的随机整数，然后除以precision得到小数
    const scaledRandom = Math.floor(Math.random() * (scaledRange + 1))
    const randomNum = numMin + scaledRandom / precision

    // 确保不超过最大值
    if (randomNum <= numMax) {
      numbers.add(randomNum)
    }

    attempts++
  }

  // 如果仍然不够，使用更高精度的浮点数填充
  while (numbers.size < count) {
    const randomNum = numMin + Math.random() * range
    numbers.add(randomNum)
  }

  // 转换为数组并排序
  const sortedNumbers = Array.from(numbers).sort((a, b) => a - b)

  // 转换为字符串，保持精度
  return sortedNumbers.map(num => {
    // 检查是否为整数
    if (Number.isInteger(num)) {
      return num.toString()
    }

    // 对于小数，使用适当的精度
    let str = num.toFixed(requiredDecimals)

    // 移除尾随的零，但保留至少defaultDecimals位小数（如果原数有小数部分）
    const parts = str.split('.')
    if (parts[1]) {
      // 移除尾随零，但至少保留1位小数
      let decimals = parts[1].replace(/0+$/, '')
      if (decimals.length === 0) {
        decimals = '0'
      }
      // 如果小数位数少于默认值，保持原有精度
      if (
        decimals.length < defaultDecimals &&
        parts[1].length >= defaultDecimals
      ) {
        decimals = parts[1].substring(
          0,
          Math.max(defaultDecimals, decimals.length)
        )
      }
      str = parts[0] + '.' + decimals
    }

    return str
  })
}

/**
 * 高性能版本：使用预分配数组和快速排序
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @param {number} count - 生成数量
 * @param {number} defaultDecimals - 默认小数位数
 * @returns {string[]} 有序的随机数字符串数组
 */
function generateOrderedRandomNumbersFast(
  min,
  max,
  count,
  defaultDecimals = 6
) {
  if (!count || count <= 0) return []

  // 参数验证和类型检查
  if (typeof min !== 'string' || typeof max !== 'string') {
    throw new Error('min and max must be strings for large number support')
  }

  // 检查是否为有效的数字字符串
  const minIsValid = /^-?\d+(\.\d+)?$/.test(min)
  const maxIsValid = /^-?\d+(\.\d+)?$/.test(max)

  if (!minIsValid || !maxIsValid) {
    throw new Error('min and max must be valid numeric strings')
  }

  // 字符串数字比较函数
  function compareNumericStrings(a, b) {
    // 处理负数
    const aIsNeg = a.startsWith('-')
    const bIsNeg = b.startsWith('-')

    if (aIsNeg && !bIsNeg) return -1
    if (!aIsNeg && bIsNeg) return 1

    // 都是正数或都是负数
    const aAbs = aIsNeg ? a.slice(1) : a
    const bAbs = bIsNeg ? b.slice(1) : b

    const [aInt, aDec = ''] = aAbs.split('.')
    const [bInt, bDec = ''] = bAbs.split('.')

    // 比较整数部分
    if (aInt.length !== bInt.length) {
      const result = aInt.length - bInt.length
      return aIsNeg ? -result : result
    }

    if (aInt !== bInt) {
      const result = aInt.localeCompare(bInt)
      return aIsNeg ? -result : result
    }

    // 整数部分相同，比较小数部分
    const maxDecLen = Math.max(aDec.length, bDec.length)
    const aPadded = aDec.padEnd(maxDecLen, '0')
    const bPadded = bDec.padEnd(maxDecLen, '0')

    const result = aPadded.localeCompare(bPadded)
    return aIsNeg ? -result : result
  }

  if (min === max) return [min]

  // 验证min < max
  if (compareNumericStrings(min, max) >= 0) {
    throw new Error('min must be less than max')
  }

  // 计算在给定精度下可能的唯一数字数量
  function calculatePossibleCount(minStr, maxStr, decimals) {
    const [minInt, minDec = ''] = minStr.split('.')
    const [maxInt, maxDec = ''] = maxStr.split('.')

    if (minInt !== maxInt) {
      // 整数部分不同，可能的数字非常多
      return Infinity
    }

    // 整数部分相同，计算小数范围
    // 使用实际需要的小数位数，取两者的最大值
    const actualDecimals = Math.max(decimals, minDec.length, maxDec.length)
    const minDecPadded = minDec.padEnd(actualDecimals, '0')
    const maxDecPadded = maxDec.padEnd(actualDecimals, '0')
    const minDecNum = parseInt(minDecPadded) || 0
    const maxDecNum = parseInt(maxDecPadded) || 0

    // 排除边界值，实际可用范围
    return Math.max(0, maxDecNum - minDecNum - 1)
  }

  // 生成随机小数的函数（排除边界值，动态调整精度）
  function generateRandomDecimal(minStr, maxStr, decimals, requiredCount = 1) {
    let currentDecimals = decimals

    // 检查当前精度下是否有足够的可能数字
    while (currentDecimals <= 15) {
      // 最多15位小数，避免精度问题
      const possibleCount = calculatePossibleCount(
        minStr,
        maxStr,
        currentDecimals
      )

      if (possibleCount >= requiredCount || possibleCount === Infinity) {
        break
      }

      currentDecimals++
    }

    // 分离整数和小数部分
    const [minInt, minDec = ''] = minStr.split('.')
    const [maxInt, maxDec = ''] = maxStr.split('.')

    // 如果整数部分不同，可以在整个范围内随机
    if (minInt !== maxInt) {
      // 生成随机整数部分
      const minBigInt = BigInt(minInt)
      const maxBigInt = BigInt(maxInt)
      const range = maxBigInt - minBigInt

      let randomInt
      let decimalPart = ''

      if (range === 0n) {
        randomInt = minBigInt
      } else {
        // 使用多个随机数来生成大范围的随机数
        const randomFactor = Math.random()
        const scaledRandom = Math.floor(Number(range) * randomFactor)
        randomInt = minBigInt + BigInt(scaledRandom)
      }

      // 根据整数部分生成合适的小数部分
      if (randomInt.toString() === minInt) {
        // 如果整数部分等于最小值，小数部分必须大于minDec
        const minDecPadded = minDec.padEnd(currentDecimals, '0')
        const minDecNum = parseInt(minDecPadded) || 0

        // 生成大于minDecNum的随机小数
        const maxPossible = Math.pow(10, currentDecimals) - 1
        if (minDecNum >= maxPossible) {
          // 如果已经是最大值，则整数部分+1，小数部分为0
          randomInt = randomInt + 1n
          decimalPart = '0'.repeat(currentDecimals)
        } else {
          const availableRange = maxPossible - minDecNum
          const randomOffset = Math.floor(Math.random() * availableRange)
          const randomDecNum = minDecNum + 1 + randomOffset
          decimalPart = randomDecNum.toString().padStart(currentDecimals, '0')
        }
      } else if (randomInt.toString() === maxInt) {
        // 如果整数部分等于最大值，小数部分必须小于maxDec
        const maxDecPadded = maxDec.padEnd(currentDecimals, '0')
        const maxDecNum = parseInt(maxDecPadded) || 0

        // 生成小于maxDecNum的随机小数
        if (maxDecNum <= 0) {
          // 如果已经是最小值，则整数部分-1，小数部分为最大值
          randomInt = randomInt - 1n
          decimalPart = '9'.repeat(currentDecimals)
        } else {
          const randomDecNum = Math.floor(Math.random() * maxDecNum)
          decimalPart = randomDecNum.toString().padStart(currentDecimals, '0')
        }
      } else {
        // 整数部分在中间范围，小数部分可以随意生成
        for (let i = 0; i < currentDecimals; i++) {
          decimalPart += Math.floor(Math.random() * 10)
        }
      }

      return randomInt.toString() + '.' + decimalPart
    } else {
      // 整数部分相同，在小数范围内随机
      // 使用实际需要的小数位数，确保不丢失精度
      const actualDecimals = Math.max(
        currentDecimals,
        minDec.length,
        maxDec.length
      )
      const minDecPadded = minDec.padEnd(actualDecimals, '0')
      const maxDecPadded = maxDec.padEnd(actualDecimals, '0')

      const minDecNum = parseInt(minDecPadded) || 0
      const maxDecNum = parseInt(maxDecPadded) || 0

      if (minDecNum >= maxDecNum) {
        // 范围太小，生成中间值
        const midDecNum = Math.floor((minDecNum + maxDecNum) / 2)
        const decimalStr = midDecNum.toString().padStart(actualDecimals, '0')
        return minInt + '.' + decimalStr
      }

      // 在范围内随机，排除边界值
      const availableRange = maxDecNum - minDecNum - 1 // 排除两个边界值

      if (availableRange <= 0) {
        // 如果没有可用范围，生成中间值
        const midDecNum = Math.floor((minDecNum + maxDecNum) / 2)
        const decimalStr = midDecNum.toString().padStart(actualDecimals, '0')
        return minInt + '.' + decimalStr
      }

      // 生成不包含边界值的随机数
      const randomOffset = Math.floor(Math.random() * availableRange)
      const randomDecNum = minDecNum + 1 + randomOffset // +1 跳过最小边界值

      const decimalStr = randomDecNum.toString().padStart(actualDecimals, '0')
      return minInt + '.' + decimalStr
    }
  }

  // 生成随机数
  const results = []
  const uniqueNumbers = new Set()

  let attempts = 0
  const maxAttempts = count * 10 // 防止无限循环

  while (results.length < count && attempts < maxAttempts) {
    const randomNum = generateRandomDecimal(min, max, defaultDecimals, count)

    if (!uniqueNumbers.has(randomNum)) {
      uniqueNumbers.add(randomNum)
      results.push(randomNum)
    }
    attempts++
  }

  // 如果仍然不够数量，填充剩余的（允许重复）
  while (results.length < count) {
    const randomNum = generateRandomDecimal(min, max, defaultDecimals, count)
    results.push(randomNum)
  }

  // 排序
  results.sort(compareNumericStrings)

  return results.slice(0, count)
}

// 字符串数字比较函数
function compareNumericStrings(a = '0', b = '0') {
  // 处理负数
  const aIsNeg = a.startsWith('-')
  const bIsNeg = b.startsWith('-')

  if (aIsNeg && !bIsNeg) return -1
  if (!aIsNeg && bIsNeg) return 1

  // 都是正数或都是负数
  const aAbs = aIsNeg ? a.slice(1) : a
  const bAbs = bIsNeg ? b.slice(1) : b

  const [aInt, aDec = ''] = aAbs.split('.')
  const [bInt, bDec = ''] = bAbs.split('.')

  // 比较整数部分
  if (aInt.length !== bInt.length) {
    const result = aInt.length - bInt.length
    return aIsNeg ? -result : result
  }

  if (aInt !== bInt) {
    const result = aInt.localeCompare(bInt)
    return aIsNeg ? -result : result
  }

  // 整数部分相同，比较小数部分
  const maxDecLen = Math.max(aDec.length, bDec.length)
  const aPadded = aDec.padEnd(maxDecLen, '0')
  const bPadded = bDec.padEnd(maxDecLen, '0')

  const result = aPadded.localeCompare(bPadded)
  return aIsNeg ? -result : result
}

// 超大字符串数字加法
function strNumAdd(num1, num2) {
  // 处理输入参数
  if (!num1) num1 = '0'
  if (!num2) num2 = '0'

  // 转换为字符串并去除前导零
  const str1 = String(num1).trim()
  const str2 = String(num2).trim()

  // 分离整数部分和小数部分
  const [int1, dec1 = ''] = str1.split('.')
  const [int2, dec2 = ''] = str2.split('.')

  // 对齐小数部分长度
  const maxDecLen = Math.max(dec1.length, dec2.length)
  const decimal1 = dec1.padEnd(maxDecLen, '0')
  const decimal2 = dec2.padEnd(maxDecLen, '0')

  // 从右到左逐位相加
  let carry = 0
  let result = ''

  // 先处理小数部分
  for (let i = maxDecLen - 1; i >= 0; i--) {
    const digit1 = parseInt(decimal1[i] || '0')
    const digit2 = parseInt(decimal2[i] || '0')
    const sum = digit1 + digit2 + carry
    result = (sum % 10) + result
    carry = Math.floor(sum / 10)
  }

  // 如果有小数部分，添加小数点
  if (maxDecLen > 0) {
    result = '.' + result
  }

  // 处理整数部分
  const maxIntLen = Math.max(int1.length, int2.length)
  const integer1 = int1.padStart(maxIntLen, '0')
  const integer2 = int2.padStart(maxIntLen, '0')

  for (let i = maxIntLen - 1; i >= 0; i--) {
    const digit1 = parseInt(integer1[i] || '0')
    const digit2 = parseInt(integer2[i] || '0')
    const sum = digit1 + digit2 + carry
    result = (sum % 10) + result
    carry = Math.floor(sum / 10)
  }

  // 处理最后的进位
  if (carry > 0) {
    result = carry + result
  }

  // 去除前导零和尾随零
  result = result.replace(/^0+/, '') || '0'
  if (result.includes('.')) {
    result = result.replace(/\.?0+$/, '')
  }

  return result
}

// 导出函数
export {
  generateOrderedRandomNumbers,
  generateOrderedRandomNumbersFast,
  strNumAdd,
  compareNumericStrings
}
